import type { AccountType } from '../types/account.js';
import type { AudienceScope, BrandSelection } from '../types/broadcast.js';

// Stage 6A v2 §2 — pure audience tree-resolution helpers, SHARED by the server
// resolver (server/modules/broadcasts/audienceService.ts) and the client
// audience tree-select (so the tree's checkbox states never disagree with what
// the server actually resolves). DOM-free + dependency-free.
//
// KEY v2 change: resolution is NO-INHERITANCE (§2.2). selectedAccountIds is
// taken LITERALLY — a selected Account contributes only its directly-linked
// contacts, never its descendants' or ancestors'. The UI cascade writes a
// Group's child Locations into selectedAccountIds explicitly; the resolver does
// NOT expand. 'all' brandSelection means the whole active scope is selected.

export type ResolverAccount = {
  id: string;
  parentAccountId: string | null;
  accountType: AccountType;
  status: 'active' | 'paused' | 'cancelled';
};

export function buildChildrenMap(
  accounts: Array<Pick<ResolverAccount, 'id' | 'parentAccountId'>>,
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const a of accounts) {
    if (a.parentAccountId === null) continue;
    const arr = map.get(a.parentAccountId);
    if (arr) arr.push(a.id);
    else map.set(a.parentAccountId, [a.id]);
  }
  return map;
}

// All descendants of rootId (excluding rootId itself), depth-first. `seen`
// guards against accidental cycles even though createAccount's assertNoCycle
// prevents them — a resolver should never infinite-loop on bad data.
export function descendantsOf(
  rootId: string,
  childrenByParent: Map<string, string[]>,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const stack = [...(childrenByParent.get(rootId) ?? [])];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    for (const child of childrenByParent.get(id) ?? []) stack.push(child);
  }
  return out;
}

// Active accounts reachable from the given brand ids (each brand + its active
// descendants). Inactive brands are skipped; inactive intermediate nodes are
// walked through structurally but filtered out of the result.
function scopeFromBrandIds(brandIds: string[], accounts: ResolverAccount[]): Set<string> {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const childrenByParent = buildChildrenMap(accounts);
  const scope = new Set<string>();
  for (const brandId of brandIds) {
    const brand = byId.get(brandId);
    if (!brand || brand.status !== 'active') continue;
    scope.add(brandId);
    for (const descId of descendantsOf(brandId, childrenByParent)) {
      if (byId.get(descId)?.status === 'active') scope.add(descId);
    }
  }
  return scope;
}

// The candidate accounts for a brand selection (§1.3): 'all' = every active
// Brand + active descendants; specific = the listed Brands + active descendants.
export function scopeAccountIds(
  brandSelection: BrandSelection,
  accounts: ResolverAccount[],
): Set<string> {
  const brandIds =
    brandSelection === 'all'
      ? accounts
          .filter((a) => a.accountType === 'brand' && a.status === 'active')
          .map((a) => a.id)
      : brandSelection.brandAccountIds;
  return scopeFromBrandIds(brandIds, accounts);
}

// Scope → the set of audience account IDs (§2.3 steps 1-2). LITERAL selection,
// no expansion: 'all' selects the whole active scope; specific keeps the
// in-scope selected accounts; both then drop the explicit exclusions.
export function resolveAudienceAccountIds(
  scope: AudienceScope,
  accounts: ResolverAccount[],
): string[] {
  const inScope = scopeAccountIds(scope.brandSelection, accounts);
  const excluded = new Set(scope.excludedAccountIds);
  const base =
    scope.brandSelection === 'all'
      ? inScope
      : new Set(scope.selectedAccountIds.filter((id) => inScope.has(id)));
  return [...base].filter((id) => !excluded.has(id));
}
